import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  ADD_MY_COURSES,
  SET_SUBSCRIPTION,
} from "../../../../apollo-client/mutations";
import { showSuccessToast } from "../../../../toast/toast-service";
import { MemberIcon } from "../../../icons/icons-svg/member-icon";
import { usePleaseLoginInfo } from "../../../../hooks/use-please-login-info";
import {
  GET_COURSE_SUBSCRIPTION,
  GET_COURSE_TEACHINGS,
  GET_USER_HEADER,
} from "../../../../apollo-client/queries";
import { useRouteMatch } from "react-router-dom";
import { QuestionMarkTooltip } from "../../../common/tooltip/question-mark-tooltip";
import { theme } from "../../../../theme";
import { useOutsideEvent } from "../../../../helpers/useOutsideEvent";
import { CONTENT_AREAS } from "../../../../constants";
import { getSelectedArea } from "../../../../services/url-helpers";

// Styled MUI components to preserve existing styling
const StyledButton = styled(Button)(({ theme, customcolor, custombackgroundcolor }) => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: '5px',
  fontWeight: 500,
  fontSize: '18px',
  padding: '5px 10px',
  alignSelf: 'center',
  cursor: 'pointer',
  color: customcolor || '#000',
  backgroundColor: custombackgroundcolor || '#fff',
  boxShadow: '1px 1px 3px rgba(0, 0, 0, 0.3)',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: custombackgroundcolor || '#fff',
    boxShadow: '2px 2px 6px rgba(0, 0, 0, 0.4)',
  },
  [theme.breakpoints.down('lg')]: {
    fontWeight: 500,
    fontSize: '13px',
    maxWidth: '160px',
  },
}));

const StyledAddCourseButton = styled(Button)(({ theme, customcolor, custombackgroundcolor }) => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: '5px',
  fontWeight: 500,
  fontSize: '18px',
  padding: '5px 10px',
  alignSelf: 'center',
  cursor: 'pointer',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: customcolor || '#000',
  backgroundColor: custombackgroundcolor || '#DFC4AD',
  boxShadow: '1px 1px 3px rgba(0, 0, 0, 0.3)',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: custombackgroundcolor || '#DFC4AD',
    boxShadow: '2px 2px 6px rgba(0, 0, 0, 0.4)',
  },
  [theme.breakpoints.down('lg')]: {
    fontWeight: 500,
    fontSize: '13px',
  },
}));

const StyledAboText = styled(Typography)({
  fontStyle: 'normal',
  display: 'flex',
  alignItems: 'center',
  textAlign: 'center',
  padding: 0,
  margin: '8px 8px 8px 8px',
});

const StyledAboMemberWrapper = styled(Box)({
  display: 'flex',
  marginRight: '10px',
  alignItems: 'center',
});

const StyledAboMemberCounter = styled(Typography)(({ theme }) => ({
  marginRight: '5px',
  fontWeight: 500,
  fontSize: '18px',
  [theme.breakpoints.down('lg')]: {
    fontWeight: 500,
    fontSize: '13px',
  },
}));

const StyledAboOptionMenu = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  width: '220px',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '15px',
  boxShadow: '-1px 2px 5px rgba(0, 0, 0, 0.3)',
  marginTop: '10px',
  right: '110px',
  backgroundColor: 'white',
  boxSizing: 'border-box',
  [theme.breakpoints.down('lg')]: {
    marginLeft: '-80px',
    right: '0px',
  },
}));

const StyledSwitchContainer = styled(Box)({
  padding: '10px 10px 10px 10px',
});

const StyledRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
});

const StyledSwitchTextWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  '&:last-child': {
    marginBottom: 0,
  },
});

const StyledSwitchText = styled(Typography)({
  marginLeft: '10px',
  cursor: 'pointer',
  fontWeight: 500,
  fontSize: '15px',
  color: '#383838',
});

const StyledQuestionMark = styled(QuestionMarkTooltip)({
  marginLeft: '20px',
});

export const AboComponent = (props) => {
  const { subscriberCount, match } = props;

  const [isAboMenuOpen, setIsAboMenuOpen] = useState(false);
  const [checkboxTutoring, setCheckboxTutoring] = useState(false);

  const isLoggedIn = Boolean(localStorage.getItem("token"));

  const routeMatch = useRouteMatch();

  const { openPleaseLoginInfo } = usePleaseLoginInfo();

  const [addMyCourses] = useMutation(ADD_MY_COURSES);
  const [setSubscription] = useMutation(SET_SUBSCRIPTION, {
    refetchQueries: [
      GET_COURSE_SUBSCRIPTION,
      GET_USER_HEADER,
      GET_COURSE_TEACHINGS,
    ],
  });

  const { data: userData, loading: loadingUserData } =
    useQuery(GET_USER_HEADER);

  const { data: courseSubscriptionsData, loading: loadingSubscriptiondata } =
    useQuery(GET_COURSE_SUBSCRIPTION, {
      variables: { course: props?.match?.params?.id },
    });

  useEffect(() => {
    setCheckboxTutoring(courseSubscriptionsData?.user?.courseSubscriptions?.[0]?.newTeachings);
  }, [courseSubscriptionsData?.user?.courseSubscriptions]);

  const isCourseSaved = Boolean(
    userData &&
      userData.user &&
      userData.user.courses.courses.find(
        (course) => course?._id.toString() === props.match?.params?.id
      )
  );

  const isContentAreaSubscribed = (courseSubscription, selectedContentArea) => {
    if (
      selectedContentArea === CONTENT_AREAS.TEACHERS &&
      courseSubscription?.newTeachings
    )
      return true;
    if (
      selectedContentArea === CONTENT_AREAS.GROUPS &&
      courseSubscription?.newGroups
    )
      return true;
    if (
      selectedContentArea === CONTENT_AREAS.CONTENT_VIDEOS &&
      courseSubscription?.newVideos
    )
      return true;
    if (
      selectedContentArea === CONTENT_AREAS.CONTENT_QUESTIONS &&
      courseSubscription?.newQuestions
    )
      return true;
    if (
      selectedContentArea === CONTENT_AREAS.CONTENT_LINKS &&
      courseSubscription?.newLinks
    )
      return true;

    return false;
  };

  const selectedContentArea = getSelectedArea(routeMatch.path);

  const isSubscribed =
    courseSubscriptionsData?.user?.courseSubscriptions?.[0] &&
    isContentAreaSubscribed(
      courseSubscriptionsData?.user?.courseSubscriptions[0],
      selectedContentArea
    );

  const intl = useIntl();
  const addCourseText = intl.formatMessage({
    id: "abo-component.add-course",
  });
  const subscribeText = intl.formatMessage({
    id: "abo-component.subscribe",
  });
  const subscribedText = intl.formatMessage({
    id: "abo-component.subscribed",
  });
  const privateTutoringText = intl.formatMessage({
    id: "general.private-tutoring",
  });

  const ref = useOutsideEvent(() => setIsAboMenuOpen(false));

  const handleAboButtonClick = () => {
    if (!isLoggedIn) {
      openPleaseLoginInfo();
      return;
    }

    if (!isCourseSaved) {
      addMyCourses({
        variables: { ids: match.params.id },
      });
    } else {
      setIsAboMenuOpen(!isAboMenuOpen);
    }
  };

  const handleCheckboxToggle = async () => {
    await setSubscription({
      variables: {
        course: match.params.id,
        newTeachings: !checkboxTutoring,
      },
    });

    showSuccessToast(
      intl.formatMessage({
        id: checkboxTutoring
          ? "abo-toast-success-deactivated"
          : "abo-toast-success",
      })
    );
  };

  if (loadingUserData || loadingSubscriptiondata) {
    return <></>;
  }
  
  return (
    <Box
      id={"abo_buttons_container"}
      ref={ref}
      data-testid="abo-component"
      sx={{
        alignSelf: 'flex-end',
        '@media (max-width: 980px)': {
          alignSelf: 'flex-end',
          marginRight: '2%',
        },
      }}
    >
      {!isCourseSaved && (
        <StyledAddCourseButton
          customcolor={"#000"}
          custombackgroundcolor={"#DFC4AD"}
          onClick={handleAboButtonClick}
          data-testid="abo-add-course-button"
        >
          <StyledAboText>{addCourseText} </StyledAboText>
          <StyledAboMemberWrapper>
            <StyledAboMemberCounter>{subscriberCount}</StyledAboMemberCounter>
            <MemberIcon color={"#000"} />
          </StyledAboMemberWrapper>
        </StyledAddCourseButton>
      )}
      {isCourseSaved && !isSubscribed && (
        <StyledButton
          customcolor={"white"}
          custombackgroundcolor={theme.mainColor}
          onClick={handleAboButtonClick}
          data-testid="abo-subscribe-button"
        >
          <StyledAboText>{subscribeText} </StyledAboText>
          <StyledAboMemberWrapper>
            <StyledAboMemberCounter>{subscriberCount}</StyledAboMemberCounter>
            <MemberIcon />
          </StyledAboMemberWrapper>
        </StyledButton>
      )}
      {isCourseSaved && isSubscribed && (
        <StyledButton
          onClick={handleAboButtonClick}
          custombackgroundcolor={"#FFFFFF"}
          data-testid="abo-subscribed-button"
        >
          <StyledAboText>{subscribedText} </StyledAboText>
          <StyledAboMemberWrapper>
            <StyledAboMemberCounter>{subscriberCount}</StyledAboMemberCounter>
            <MemberIcon color={"#000"} />
          </StyledAboMemberWrapper>
        </StyledButton>
      )}
      {isAboMenuOpen && (
        <StyledAboOptionMenu data-testid="abo-option-menu">
          <StyledSwitchContainer>
            <StyledRow>
              <StyledSwitchTextWrapper>
                <FormControlLabel
                  control={
                    <Switch
                      checked={checkboxTutoring}
                      onChange={handleCheckboxToggle}
                      data-testid="abo-tutoring-switch"
                    />
                  }
                  label={
                    <StyledSwitchText onClick={handleCheckboxToggle}>
                      {privateTutoringText}
                    </StyledSwitchText>
                  }
                />
              </StyledSwitchTextWrapper>
              <StyledQuestionMark tooltipId="abo-component.hint" />
            </StyledRow>
          </StyledSwitchContainer>
        </StyledAboOptionMenu>
      )}
    </Box>
  );
};
